import React, { useState } from 'react'
import { SpecializedChatbotProps, ChatMessage, ChatApiResponse } from '../../interfaces'
import { ChatMessage as ChatMessageComponent, LoadingMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import styles from './styles.module.scss'
import api from '../../../../../helpers/apiRequester'

export const SpecializedChatbot: React.FC<SpecializedChatbotProps> = ({
    session,
    setSession,
    onSaveConversation,
    chatbotType,
    title,
    welcomeMessage,
    placeholder,
    onClearSession,
}) => {
    const [currentMessage, setCurrentMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const getApiEndpoint = () => {
        switch (chatbotType) {
            case 'supreme-court':
                return 'knowledge-base/supreme-court-chat'
            case 'financial-ombudsman':
                return 'knowledge-base/financial-ombudsman-chat'
            default:
                return 'knowledge-base/rag-chat'
        }
    }

    const handleSendMessage = async () => {
        if (!currentMessage.trim()) return

        console.log('currentMessage', currentMessage)
        console.log('chatbotType', chatbotType)
        console.log('current sessionId', session.sessionId)

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            content: currentMessage,
            sender: 'user',
            timestamp: new Date().toISOString(),
        }

        // Add user message to session
        setSession((prev) => ({
            ...prev,
            messages: [...prev.messages, userMessage],
        }))

        const messageToProcess = currentMessage
        setCurrentMessage('')
        setIsLoading(true)

        try {
            // Prepare API payload
            const apiPayload: any = {
                chat_messages: [messageToProcess],
                source: chatbotType,
            }

            // Include session ID if we have one
            if (session.sessionId) {
                apiPayload.session_id = session.sessionId
            }

            console.log('API payload:', apiPayload)

            const apiResponse = await api({
                url: getApiEndpoint(),
                data: apiPayload,
            })

            const data: ChatApiResponse = apiResponse.data
            console.log('API response data:', data)

            const assistantMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                content: data.completion,
                sender: 'assistant',
                timestamp: new Date().toISOString(),
            }

            // Update session with assistant message and potentially new session ID
            setSession((prev) => ({
                sessionId: data.sessionId || prev.sessionId,
                messages: [...prev.messages, assistantMessage],
            }))

            console.log('Updated session ID:', data.sessionId || session.sessionId)
        } catch (error) {
            console.error('Error sending message:', error)
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                content: 'Sorry, I encountered an error while processing your request. Please try again.',
                sender: 'assistant',
                timestamp: new Date().toISOString(),
            }

            setSession((prev) => ({
                ...prev,
                messages: [...prev.messages, errorMessage],
            }))
        } finally {
            setIsLoading(false)
        }
    }

    const handleClearConversation = () => {
        if (
            session.messages.length > 0 &&
            window.confirm('Are you sure you want to clear this conversation? This will start a new session.')
        ) {
            onClearSession()
        }
    }

    return (
        <div className={styles.ragChatbotContainer}>
            <div className={styles.chatHeader}>
                <div className={styles.headerLeft}>
                    <h3>{title}</h3>
                    {session.sessionId && (
                        <span className={styles.sessionId}>Session: {session.sessionId.substring(0, 8)}...</span>
                    )}
                </div>
                <div className={styles.headerActions}>
                    <button
                        onClick={handleClearConversation}
                        disabled={session.messages.length === 0}
                        className={styles.clearBtn}
                    >
                        Clear Chat
                    </button>
                    <button
                        onClick={onSaveConversation}
                        disabled={session.messages.length === 0}
                        className={styles.saveBtn}
                    >
                        Save Conversation
                    </button>
                </div>
            </div>

            <div className={styles.chatContainer}>
                <div className={styles.messagesContainer}>
                    {session.messages.length === 0 ? (
                        <div className={styles.welcomeMessage}>
                            <p>{welcomeMessage}</p>
                        </div>
                    ) : (
                        session.messages.map((message) => <ChatMessageComponent key={message.id} message={message} />)
                    )}
                    {isLoading && <LoadingMessage />}
                </div>

                <ChatInput
                    currentMessage={currentMessage}
                    setCurrentMessage={setCurrentMessage}
                    onSendMessage={handleSendMessage}
                    isLoading={isLoading}
                    placeholder={placeholder}
                />
            </div>
        </div>
    )
}
